import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBIcon,
  MDBInput,
  MDBRow,
  MDBSelect,
  MDBSpinner,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { captureError } from '../../utils/sentryHelpers';
import {
  compareMediaItemsByDate,
  compareMediaSeries,
  filterMediaItems,
  getLocalized,
  mediathekImageUrl,
} from '../../utils/mediathek';
import { DELETE_MEDIA_ITEM, LIST_MEDIA_ITEMS, LIST_MEDIA_SERIES_OPTIONS, fetchAllPages } from './mediathekGraphql';
import { ConfirmModal, DuplicateButton, ErrorList, FlashToast, StatusBadge, TypeBadge, selectValue, useFlashToast } from './MediathekShared';

const client = generateClient();
const COMPONENT = 'MediaItemList';
const PAGE_SIZE = 50;
const RETURN_STATE = { from: '/admin/mediathek/items' };

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');

export default function MediaItemList() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [filters, setFilters] = useState({ seriesId: '', type: '', status: '', search: '' });
  const [page, setPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useFlashToast();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setErrors([]);
    try {
      const [allItems, allSeries] = await Promise.all([
        fetchAllPages(client, LIST_MEDIA_ITEMS, {}, 'listMediaItems'),
        fetchAllPages(client, LIST_MEDIA_SERIES_OPTIONS, {}, 'listMediaSeries'),
      ]);
      setItems(allItems.sort(compareMediaItemsByDate));
      setSeriesList(allSeries.sort(compareMediaSeries));
    } catch (error) {
      console.error('Error fetching media items:', error);
      captureError(error, { feature: 'mediathek', operation: 'fetch-item-list', tags: { component: COMPONENT } });
      setErrors(['The items could not be loaded']);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const seriesById = useMemo(() => Object.fromEntries(seriesList.map((s) => [s.id, s])), [seriesList]);
  const filtered = useMemo(() => filterMediaItems(items, filters, seriesById), [items, filters, seriesById]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const updateFilter = (field, value) => {
    setFilters((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
    setPage(0);
  };

  const seriesOptions = useMemo(() => [
    { text: t('All series'), value: '' },
    ...seriesList.map((s) => ({ text: getLocalized(s.title, i18n.language) || s.slug || s.id, value: s.id })),
  ], [seriesList, i18n.language, t]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await client.graphql({ query: DELETE_MEDIA_ITEM, variables: { input: { id: deleteTarget.id } } });
      setItems((prev) => prev.filter((entry) => entry.id !== deleteTarget.id));
      setToast('Item deleted');
    } catch (error) {
      console.error('Error deleting media item:', error);
      captureError(error, {
        feature: 'mediathek',
        operation: 'delete-item',
        context: { itemId: deleteTarget.id },
        tags: { component: COMPONENT },
      });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The item could not be deleted']);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0">{t('Media library')}: {t('Items')}</h5>
            <Link to="/admin/mediathek">
              <MDBBtn color="secondary" outline>
                <MDBIcon fas icon="layer-group" className="me-2" />
                {t('Series & formats')}
              </MDBBtn>
            </Link>
          </div>

          <MDBRow className="g-3 mb-3">
            <MDBCol md="6" xl="3">
              {!isLoading && (
                <MDBSelect
                  label={t('Series')}
                  search
                  searchLabel={t('Search')}
                  data={seriesOptions}
                  value={filters.seriesId}
                  onChange={(entry) => {
                    const value = selectValue(entry);
                    if (value !== undefined) updateFilter('seriesId', value || '');
                  }}
                />
              )}
            </MDBCol>
            <MDBCol md="6" xl="2">
              <MDBSelect
                label={t('Type')}
                data={[
                  { text: t('All types'), value: '' },
                  { text: t('Podcast'), value: 'podcast' },
                  { text: t('Video'), value: 'video' },
                ]}
                value={filters.type}
                onChange={(entry) => {
                  const value = selectValue(entry);
                  if (value !== undefined) updateFilter('type', value || '');
                }}
              />
            </MDBCol>
            <MDBCol md="6" xl="2">
              <MDBSelect
                label={t('Status')}
                data={[
                  { text: t('All statuses'), value: '' },
                  { text: t('Draft'), value: 'draft' },
                  { text: t('Published'), value: 'published' },
                ]}
                value={filters.status}
                onChange={(entry) => {
                  const value = selectValue(entry);
                  if (value !== undefined) updateFilter('status', value || '');
                }}
              />
            </MDBCol>
            <MDBCol md="6" xl="5">
              <MDBInput
                label={t('Search title, guest or company')}
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
              />
            </MDBCol>
          </MDBRow>

          <ErrorList errors={errors} />

          {isLoading ? (
            <div className="text-center py-5">
              <MDBSpinner color="primary" />
            </div>
          ) : (
            <>
              <div className="text-muted small mb-2">
                {t('{{shown}} of {{total}} items', { shown: filtered.length, total: items.length })}
              </div>
              {filtered.length === 0 ? (
                <p className="text-muted mb-0">{t('No items found.')}</p>
              ) : (
                <MDBTable hover responsive align="middle" small>
                  <MDBTableHead>
                    <tr>
                      <th style={{ width: '60px' }}></th>
                      <th>{t('Title')}</th>
                      <th>{t('Series')}</th>
                      <th>{t('Published at')}</th>
                      <th className="text-end">{t('Episode')}</th>
                      <th>{t('Guest / company')}</th>
                      <th>{t('Status')}</th>
                      <th>{t('Media')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {visible.map((entry) => {
                      const series = seriesById[entry.mediaSeriesId];
                      return (
                        <tr key={entry.id}>
                          <td>
                            {entry.image?.fileName ? (
                              <img
                                src={mediathekImageUrl(entry.image.fileName)}
                                alt=""
                                className="rounded"
                                style={{ width: '48px', height: '36px', objectFit: 'cover' }}
                              />
                            ) : null}
                          </td>
                          <td>
                            <Link to={`/admin/mediathek/items/${entry.id}`} state={RETURN_STATE} className="fw-bold">
                              {getLocalized(entry.title, i18n.language) || t('(untitled)')}
                            </Link>
                            {entry.isHero && <MDBIcon fas icon="star" className="text-warning ms-2" title={t('Hero')} />}
                            {entry.slug && <div className="text-muted small"><code>{entry.slug}</code></div>}
                          </td>
                          <td>
                            {series ? (
                              <>
                                <Link to={`/admin/mediathek/series/${series.id}`}>
                                  {getLocalized(series.title, i18n.language) || series.slug}
                                </Link>
                                <div><TypeBadge type={series.type} /></div>
                              </>
                            ) : (
                              <span className="text-danger small">{t('Unknown series')}</span>
                            )}
                          </td>
                          <td>{formatDate(entry.publishedAt)}</td>
                          <td className="text-end">{entry.episodeNumber ?? '-'}</td>
                          <td>
                            {entry.guest && <div>{entry.guest}</div>}
                            {entry.companyName && <div className="text-muted small">{entry.companyName}</div>}
                          </td>
                          <td><StatusBadge status={entry.status} /></td>
                          <td className="text-nowrap">
                            {entry.audio?.s3Path && <MDBIcon fas icon="headphones" className="me-2" title={t('Audio')} />}
                            {entry.video?.s3Path && <MDBIcon fas icon="film" className="me-2" title={t('Video file')} />}
                            {entry.externalVideoUrl && <MDBIcon fab icon="youtube" className="me-2" title={t('External video')} />}
                          </td>
                          <td className="text-nowrap">
                            <Link to={`/admin/mediathek/items/${entry.id}`} state={RETURN_STATE}>
                              <MDBBtn color="link" size="sm" title={t('Edit')}>
                                <MDBIcon fas icon="edit" />
                              </MDBBtn>
                            </Link>
                            <DuplicateButton
                              variant="icon"
                              item={entry}
                              from={RETURN_STATE.from}
                              label={`${t('Duplicate')}: ${getLocalized(entry.title, i18n.language) || t('(untitled)')}`}
                            />
                            <MDBBtn color="link" size="sm" title={t('Delete')} aria-label={t('Delete')} onClick={() => setDeleteTarget(entry)}>
                              <MDBIcon fas icon="trash" className="text-danger" aria-hidden="true" />
                            </MDBBtn>
                          </td>
                        </tr>
                      );
                    })}
                  </MDBTableBody>
                </MDBTable>
              )}
              {pageCount > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-3 mt-2">
                  <MDBBtn color="secondary" outline size="sm" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                    <MDBIcon fas icon="chevron-left" />
                  </MDBBtn>
                  <span>{t('Page {{page}} of {{pages}}', { page: currentPage + 1, pages: pageCount })}</span>
                  <MDBBtn color="secondary" outline size="sm" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}>
                    <MDBIcon fas icon="chevron-right" />
                  </MDBBtn>
                </div>
              )}
            </>
          )}
        </MDBCardBody>
      </MDBCard>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('Delete item')}
        message={t('Are you sure you want to delete the item "{{title}}"? Audio and video files stay in storage.', { title: getLocalized(deleteTarget?.title, i18n.language) })}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        busy={isDeleting}
      />
      <FlashToast message={toast} onClose={() => setToast(null)} />
    </>
  );
}
